/**
 * Influencer Detail — editable profile, referral stats and promo codes.
 * Mirrors the customer detail layout: profile header, stat cards, editable form, codes table.
 */
import React, { useState, useEffect, useCallback } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { CircularProgress, Switch } from '@mui/joy'
import {
    ArrowBack,
    Campaign,
    People,
    LocalOffer,
    Redeem,
    Save,
    Person,
    CalendarToday,
    VpnKey,
    DeleteOutline,
    Link as LinkIcon,
    ContentCopy,
    Check,
} from '@mui/icons-material'
import { motion } from 'framer-motion'
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
import { SubscriptionPlan } from '@numeo/types-core'
import { isAxiosError } from 'axios'
import { billingApi } from './api'
import { useInfluencers } from './InfluencersContext'
import { UpdateInfluencerRequest, InfluencerTrialPlan } from './types'
import ConfirmDeleteModal from './ConfirmDeleteModal'

dayjs.extend(relativeTime)

const ease = [0.23, 1, 0.32, 1]
const TIERS = ['Silver', 'Gold', 'Platinum']

function getInitials(name: string): string {
    if (!name) return '?'
    const parts = name.trim().split(/\s+/)
    if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase()
    return name.substring(0, 2).toUpperCase()
}

interface FormState {
    name: string
    email: string
    platform: string
    tier: string
    commissionPercent: string
    notes: string
    isPortalEnabled: boolean
    password: string
    trialDays: string
    trialPlan: InfluencerTrialPlan
}

export default function InfluencerDetailPage() {
    const { id } = useParams<{ id: string }>()
    const navigate = useNavigate()
    const { getById, loading, error, ensureLoaded, applyUpdate, applyDelete } = useInfluencers()
    const detail = id ? getById(id) : undefined
    const [form, setForm] = useState<FormState | null>(null)
    const [saving, setSaving] = useState(false)
    const [saveSuccess, setSaveSuccess] = useState<string | null>(null)
    const [saveError, setSaveError] = useState<string | null>(null)
    const [deleteOpen, setDeleteOpen] = useState(false)
    const [deleting, setDeleting] = useState(false)
    const [deleteError, setDeleteError] = useState<string | null>(null)
    const [copied, setCopied] = useState(false)

    const handleCopyLink = useCallback(async () => {
        if (!detail?.referralLink) return
        try {
            await navigator.clipboard.writeText(detail.referralLink)
            setCopied(true)
            setTimeout(() => setCopied(false), 2000)
        } catch {
            // Clipboard unavailable (e.g. insecure context) — no-op; the link is selectable in the field.
        }
    }, [detail?.referralLink])

    // Cache already holds everything from the list. Only fetch on direct load / refresh.
    useEffect(() => {
        ensureLoaded()
    }, [ensureLoaded])

    // Initialize the form once the influencer is available in the cache.
    useEffect(() => {
        if (detail && !form) {
            setForm({
                name: detail.name,
                email: detail.email || '',
                platform: detail.platform || '',
                tier: detail.tier,
                commissionPercent: String(detail.commissionPercent),
                notes: detail.notes || '',
                isPortalEnabled: detail.isPortalEnabled,
                password: '',
                trialDays: String(detail.trialDays ?? 30),
                trialPlan: detail.trialPlan ?? SubscriptionPlan.PRO,
            })
        }
    }, [detail, form])

    const updateField = <K extends keyof FormState>(key: K, value: FormState[K]) => {
        setForm((prev) => (prev ? { ...prev, [key]: value } : prev))
    }

    const handleSave = useCallback(async () => {
        if (!id || !form) return
        const pct = Number(form.commissionPercent)
        if (Number.isNaN(pct) || pct < 0 || pct > 100) {
            setSaveError('Commission must be between 0 and 100')
            return
        }
        const days = Number(form.trialDays)
        if (!Number.isInteger(days) || days < 1 || days > 365) {
            setSaveError('Trial length must be between 1 and 365 days')
            return
        }
        try {
            setSaving(true)
            setSaveError(null)
            setSaveSuccess(null)
            const payload: UpdateInfluencerRequest = {
                name: form.name.trim(),
                email: form.email.trim() || undefined,
                platform: form.platform.trim() || undefined,
                tier: form.tier,
                commissionPercent: pct,
                notes: form.notes.trim() || undefined,
                isPortalEnabled: form.isPortalEnabled,
                trialDays: days,
                trialPlan: form.trialPlan,
            }
            if (form.password.trim()) payload.password = form.password.trim()
            await billingApi.updateInfluencer(id, payload)
            // Patch the shared cache — no refetch (promo codes/stats are unchanged).
            applyUpdate(id, {
                name: payload.name || form.name,
                email: payload.email ?? null,
                platform: payload.platform ?? null,
                tier: form.tier,
                commissionPercent: pct,
                notes: payload.notes ?? null,
                isPortalEnabled: form.isPortalEnabled,
                trialDays: days,
                trialPlan: form.trialPlan,
            })
            setSaveSuccess('Influencer updated')
            updateField('password', '')
        } catch (err: unknown) {
            setSaveError((isAxiosError<{ message?: string }>(err) && err.response?.data?.message) || 'Failed to update influencer')
        } finally {
            setSaving(false)
        }
    }, [id, form, applyUpdate])

    const handleDelete = useCallback(async () => {
        if (!id) return
        try {
            setDeleting(true)
            setDeleteError(null)
            await billingApi.deleteInfluencer(id)
            applyDelete(id)
            navigate('/billing/influencers')
        } catch (err: unknown) {
            setDeleteError((isAxiosError<{ message?: string }>(err) && err.response?.data?.message) || 'Failed to delete influencer')
            setDeleting(false)
        }
    }, [id, applyDelete, navigate])

    if (!detail) {
        if (loading) {
            return (
                <div className="flex items-center justify-center py-24">
                    <CircularProgress />
                </div>
            )
        }
        return (
            <div className="p-6">
                <button onClick={() => navigate('/billing/influencers')} className="text-sm text-gray-500 hover:text-indigo-600 flex items-center gap-1 mb-4">
                    <ArrowBack sx={{ fontSize: 16 }} /> Back to Influencers
                </button>
                <div className="text-sm px-3 py-2 rounded-lg bg-red-50 text-red-700">{error || 'Influencer not found'}</div>
            </div>
        )
    }

    if (!form) {
        return (
            <div className="flex items-center justify-center py-24">
                <CircularProgress />
            </div>
        )
    }

    const STAT_CARDS = [
        {
            label: 'Users Joined',
            value: detail.usersJoined,
            color: 'border-t-emerald-500',
            iconBg: 'bg-emerald-50',
            iconColor: 'text-emerald-600',
            Icon: People,
        },
        { label: 'Promo Codes', value: detail.promoCodeCount, color: 'border-t-blue-500', iconBg: 'bg-blue-50', iconColor: 'text-blue-600', Icon: LocalOffer },
        {
            label: 'Total Redemptions',
            value: detail.totalRedemptions,
            color: 'border-t-violet-500',
            iconBg: 'bg-violet-50',
            iconColor: 'text-violet-600',
            Icon: Redeem,
        },
    ]

    return (
        <div className="p-6 w-full">
            {/* Breadcrumb / Back */}
            <button onClick={() => navigate('/billing/influencers')} className="text-sm text-gray-500 hover:text-indigo-600 flex items-center gap-1 mb-4">
                <ArrowBack sx={{ fontSize: 16 }} /> Back to Influencers
            </button>

            {/* Profile Header */}
            <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, ease }}
                className="flex items-center gap-4 mb-6"
            >
                <div className="w-14 h-14 rounded-full bg-indigo-100 text-indigo-700 flex items-center justify-center text-lg font-bold">
                    {getInitials(detail.name)}
                </div>
                <div>
                    <h1 className="text-xl font-bold text-gray-900">{detail.name}</h1>
                    <div className="flex items-center gap-3 text-sm text-gray-500">
                        <span className="flex items-center gap-1">
                            <Campaign sx={{ fontSize: 14 }} /> {detail.tier} · {detail.commissionPercent}% commission
                        </span>
                        <span className="flex items-center gap-1">
                            <CalendarToday sx={{ fontSize: 13 }} /> Joined {dayjs(detail.createdAt).format('MMM D, YYYY')}
                        </span>
                        {detail.lastLogin && <span className="text-gray-400">Last login {dayjs(detail.lastLogin).fromNow()}</span>}
                    </div>
                </div>
                <button
                    onClick={() => {
                        setDeleteError(null)
                        setDeleteOpen(true)
                    }}
                    className="ml-auto h-9 px-3 text-sm font-medium border border-gray-200 text-gray-600 rounded-lg hover:bg-red-50 hover:text-red-600 hover:border-red-200 transition-colors flex items-center gap-1.5"
                >
                    <DeleteOutline sx={{ fontSize: 16 }} />
                    Delete
                </button>
            </motion.div>

            {/* Stat Cards */}
            <div className="grid grid-cols-3 gap-3 mb-6">
                {STAT_CARDS.map((stat, i) => (
                    <motion.div
                        key={stat.label}
                        initial={{ opacity: 0, y: 12 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.3, delay: i * 0.05, ease }}
                    >
                        <div className={`bg-white rounded-xl border border-gray-100 shadow-sm border-t-[3px] ${stat.color} p-4`}>
                            <div className="flex items-center justify-between mb-2">
                                <p className="text-[11px] font-semibold text-gray-400 tracking-wider uppercase">{stat.label}</p>
                                <div className={`w-7 h-7 rounded-md ${stat.iconBg} flex items-center justify-center`}>
                                    <stat.Icon sx={{ fontSize: 14 }} className={stat.iconColor} />
                                </div>
                            </div>
                            <p className="text-2xl font-bold text-gray-900">{stat.value}</p>
                        </div>
                    </motion.div>
                ))}
            </div>

            <div className="grid grid-cols-3 gap-6">
                {/* Edit Form */}
                <motion.div
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3, delay: 0.15, ease }}
                    className="col-span-1"
                >
                    <div className="bg-white rounded-xl border border-gray-100 shadow-sm">
                        <div className="px-5 py-3 border-b border-gray-100 flex items-center gap-2">
                            <Person sx={{ fontSize: 16 }} className="text-gray-500" />
                            <p className="text-sm font-semibold text-gray-900">Profile</p>
                        </div>
                        <div className="p-4 space-y-3">
                            {saveSuccess && <div className="text-sm px-3 py-2 rounded-lg bg-emerald-50 text-emerald-700">{saveSuccess}</div>}
                            {saveError && <div className="text-sm px-3 py-2 rounded-lg bg-red-50 text-red-700">{saveError}</div>}

                            <div>
                                <label className="text-xs font-medium text-gray-600 mb-1.5 block">Name</label>
                                <input
                                    value={form.name}
                                    onChange={(e) => updateField('name', e.target.value)}
                                    className="w-full h-9 text-sm px-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-300"
                                />
                            </div>
                            <div>
                                <label className="text-xs font-medium text-gray-600 mb-1.5 block">Email</label>
                                <input
                                    value={form.email}
                                    onChange={(e) => updateField('email', e.target.value)}
                                    className="w-full h-9 text-sm px-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-300"
                                />
                            </div>
                            <div>
                                <label className="text-xs font-medium text-gray-600 mb-1.5 block">Platform</label>
                                <input
                                    placeholder="YouTube, TikTok, …"
                                    value={form.platform}
                                    onChange={(e) => updateField('platform', e.target.value)}
                                    className="w-full h-9 text-sm px-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-300"
                                />
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="text-xs font-medium text-gray-600 mb-1.5 block">Tier</label>
                                    <select
                                        value={form.tier}
                                        onChange={(e) => updateField('tier', e.target.value)}
                                        className="w-full h-9 text-sm px-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 bg-white"
                                    >
                                        {TIERS.map((t) => (
                                            <option key={t} value={t}>
                                                {t}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div>
                                    <label className="text-xs font-medium text-gray-600 mb-1.5 block">Commission %</label>
                                    <input
                                        type="number"
                                        min={0}
                                        max={100}
                                        value={form.commissionPercent}
                                        onChange={(e) => updateField('commissionPercent', e.target.value)}
                                        className="w-full h-9 text-sm px-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-300"
                                    />
                                </div>
                            </div>
                            <div>
                                <label className="text-xs font-medium text-gray-600 mb-1.5 block">Notes</label>
                                <textarea
                                    rows={3}
                                    value={form.notes}
                                    onChange={(e) => updateField('notes', e.target.value)}
                                    className="w-full text-sm px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-300 resize-none"
                                />
                            </div>

                            {/* Referral link */}
                            <div className="border-t border-gray-100 pt-3">
                                <label className="text-xs font-medium text-gray-600 mb-1.5 flex items-center gap-1">
                                    <LinkIcon sx={{ fontSize: 14 }} /> Referral link
                                </label>
                                <div className="flex gap-2">
                                    <input
                                        readOnly
                                        value={detail.referralLink || ''}
                                        onFocus={(e) => e.target.select()}
                                        className="w-full flex-1 h-9 text-xs px-3 border border-gray-200 rounded-lg bg-gray-50 text-gray-700 font-mono focus:outline-none"
                                    />
                                    <button
                                        type="button"
                                        onClick={handleCopyLink}
                                        disabled={!detail.referralLink}
                                        className="h-9 px-3 text-xs font-medium border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50 flex items-center gap-1 whitespace-nowrap"
                                    >
                                        {copied ? <Check sx={{ fontSize: 14 }} /> : <ContentCopy sx={{ fontSize: 14 }} />}
                                        {copied ? 'Copied' : 'Copy'}
                                    </button>
                                </div>
                                <p className="text-[11px] text-gray-400 mt-1">New signups via this link get the trial below when they connect their email.</p>
                                <div className="mt-2 flex items-center gap-3 text-[11px] text-gray-500">
                                    <span>
                                        <span className="font-semibold text-gray-700">{detail.referralLinkSignups}</span> link signups
                                    </span>
                                    <span>
                                        <span className="font-semibold text-gray-700">{detail.referralConversions}</span> converted
                                    </span>
                                    <span>
                                        <span className="font-semibold text-emerald-600">${(detail.commissionEarnedCents / 100).toFixed(2)}</span> commission
                                    </span>
                                </div>
                            </div>

                            {/* Referral trial config */}
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="text-xs font-medium text-gray-600 mb-1.5 block">Trial length (days)</label>
                                    <input
                                        type="number"
                                        min={1}
                                        max={365}
                                        value={form.trialDays}
                                        onChange={(e) => updateField('trialDays', e.target.value)}
                                        className="w-full h-9 text-sm px-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-300"
                                    />
                                </div>
                                <div>
                                    <label className="text-xs font-medium text-gray-600 mb-1.5 block">Trial plan</label>
                                    <select
                                        value={form.trialPlan}
                                        onChange={(e) => updateField('trialPlan', e.target.value as InfluencerTrialPlan)}
                                        className="w-full h-9 text-sm px-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 bg-white"
                                    >
                                        <option value={SubscriptionPlan.PRO}>Pro</option>
                                        <option value={SubscriptionPlan.ULTRA}>Ultra</option>
                                    </select>
                                </div>
                            </div>

                            {/* Portal access */}
                            <div className="border-t border-gray-100 pt-3">
                                <div className="flex items-center justify-between mb-2">
                                    <span className="text-xs font-medium text-gray-600 flex items-center gap-1">
                                        <VpnKey sx={{ fontSize: 14 }} /> Portal Access
                                    </span>
                                    <Switch checked={form.isPortalEnabled} onChange={(e) => updateField('isPortalEnabled', e.target.checked)} />
                                </div>
                                <input
                                    type="password"
                                    placeholder="Set / reset portal password"
                                    value={form.password}
                                    onChange={(e) => updateField('password', e.target.value)}
                                    className="w-full h-9 text-sm px-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-300"
                                />
                                <p className="text-[11px] text-gray-400 mt-1">Leave blank to keep the current password.</p>
                            </div>

                            <button
                                onClick={handleSave}
                                disabled={saving}
                                className="w-full h-9 px-4 text-sm font-medium bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-1.5"
                            >
                                {saving ? <CircularProgress size="sm" sx={{ '--CircularProgress-size': '14px' }} /> : <Save sx={{ fontSize: 14 }} />}
                                Save Changes
                            </button>
                        </div>
                    </div>
                </motion.div>

                {/* Promo Codes */}
                <motion.div
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3, delay: 0.2, ease }}
                    className="col-span-2"
                >
                    <div className="bg-white rounded-xl border border-gray-100 shadow-sm">
                        <div className="px-5 py-3 border-b border-gray-100 flex items-center gap-2">
                            <LocalOffer sx={{ fontSize: 16 }} className="text-gray-500" />
                            <p className="text-sm font-semibold text-gray-900">Promo Codes</p>
                            <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full font-medium">{detail.promoCodes.length}</span>
                        </div>
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead>
                                    <tr className="bg-gray-50/80 text-left">
                                        <th className="px-5 py-3 text-xs font-semibold text-gray-600">Code</th>
                                        <th className="px-2 py-3 text-xs font-semibold text-gray-600">Discount</th>
                                        <th className="px-2 py-3 text-xs font-semibold text-gray-600">Plans</th>
                                        <th className="px-2 py-3 text-xs font-semibold text-gray-600 text-right">Users Joined</th>
                                        <th className="px-2 py-3 text-xs font-semibold text-gray-600 text-right">Redeemed</th>
                                        <th className="px-2 py-3 text-xs font-semibold text-gray-600 text-right pr-5">Status</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {detail.promoCodes.length === 0 ? (
                                        <tr>
                                            <td colSpan={6} className="text-center py-12 text-gray-400 text-sm">
                                                No promo codes yet
                                            </td>
                                        </tr>
                                    ) : (
                                        detail.promoCodes.map((pc) => (
                                            <tr key={pc.id} className="border-b border-gray-100 last:border-b-0">
                                                <td className="py-2.5 pl-5 pr-2 font-mono font-medium text-gray-800">{pc.code}</td>
                                                <td className="py-2.5 px-2 text-gray-600">
                                                    {pc.trialDays ? `${pc.trialDays}d trial` : `${pc.discountPercent}%`}
                                                </td>
                                                <td className="py-2.5 px-2">
                                                    <div className="flex gap-1">
                                                        {pc.plans.map((p) => (
                                                            <span key={p} className="text-[10px] font-bold px-2 py-0.5 rounded bg-blue-100 text-blue-700">
                                                                {p}
                                                            </span>
                                                        ))}
                                                    </div>
                                                </td>
                                                <td className="py-2.5 px-2 text-right font-semibold text-gray-800">{pc.usersJoined}</td>
                                                <td className="py-2.5 px-2 text-right text-gray-600">{pc.timesRedeemed}</td>
                                                <td className="py-2.5 px-2 text-right pr-5">
                                                    <span
                                                        className={`text-[10px] px-2 py-0.5 rounded ${pc.active ? 'bg-emerald-50 text-emerald-700' : 'bg-gray-100 text-gray-500'}`}
                                                    >
                                                        {pc.active ? 'Active' : 'Inactive'}
                                                    </span>
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </motion.div>
            </div>

            <ConfirmDeleteModal
                open={deleteOpen}
                title="Delete influencer"
                message={
                    <span>
                        Delete <strong>{detail.name}</strong>?
                        {detail.promoCodeCount > 0 && (
                            <>
                                {' '}
                                Their {detail.promoCodeCount} promo code{detail.promoCodeCount > 1 ? 's' : ''} will remain in Stripe but become unlinked.
                            </>
                        )}
                        {deleteError && <div className="mt-2 font-medium">{deleteError}</div>}
                    </span>
                }
                loading={deleting}
                onConfirm={handleDelete}
                onClose={() => setDeleteOpen(false)}
            />
        </div>
    )
}
